import { useState, useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useAppContext } from '../../../context/AppContext';
import { favoritesService } from '../../../services';
import { getProfile } from '../../../services';
import { supabase } from '../../../services/supabase/client';
import applicationService from '../../../services/applicationService';
import {
  getApplyFormData,
  hasCompleteApplyFormData,
} from '../../../services/applyFormService';
import { getAllPositionStatus } from '../../../services/positionService';
import { getApplicationStatus } from '../../../utils/applicationStorage';
import { getCurrentUser } from '../../../utils/currentUser';
import { getCollaborationDataById } from '../../favorites/utils/favoritesHelpers';
import { getCollaborationById } from '../../../services/collaborationService';
import {
  processProjectData,
  getPositionsData,
} from '../utils/collaborationHelpers';

export const useCollaborationData = () => {
  const { state } = useAppContext();
  const location = useLocation();
  const { id } = useParams();

  // 🔧 【数据完整性检查】检查数据是否完整（必须包含关键字段）
  const isDataComplete = data => {
    if (!data) return false;

    // 检查关键字段是否存在且不为空
    // 如果只有 title，其他字段都是空的，说明数据不完整
    const hasTitle = data.title && data.title.trim() !== '';
    const hasDescription = data.description && data.description.trim() !== '';
    const hasRoles =
      data.roles && Array.isArray(data.roles) && data.roles.length > 0;
    const hasAuthor = data.author && data.author.id;

    // 如果只有 title，没有其他关键字段，认为数据不完整
    if (hasTitle && !hasDescription && !hasRoles && !hasAuthor) {
      console.warn(
        '[useCollaborationData] ⚠️ Data incomplete: only has title, missing other fields'
      );
      return false;
    }

    // 至少要有 title 和 author
    return hasTitle && hasAuthor;
  };

  // 从Context、路由状态、URL参数获取项目数据
  const [projectData, setProjectData] = useState(() => {
    // 优先从 Context 或 location.state 获取
    // 支持两种格式：location.state.project 或 location.state 直接包含数据
    const initialData =
      state.selectedCollaboration ||
      location.state?.project ||
      (location.state?.id ? location.state : null);

    // 🔧 【关键修复】检查数据完整性，如果不完整，返回 null，等待 Supabase 查询
    if (initialData && !isDataComplete(initialData)) {
      console.warn(
        '[useCollaborationData] ⚠️ Initial data incomplete, ignoring and waiting for Supabase query',
        initialData
      );
      return null; // 返回 null，这样 isLoading 会被设置为 true
    }

    // 如果没有初始数据但有 ID，尝试从 sessionStorage 获取（通知点击时存储的数据）
    if (!initialData && id) {
      try {
        const sessionData = sessionStorage.getItem(`collaboration_${id}`);
        if (sessionData) {
          const parsedData = JSON.parse(sessionData);
          console.log(
            '[useCollaborationData] Found data in sessionStorage:',
            parsedData
          );

          // 🔧 【关键修复】检查 sessionStorage 数据完整性
          if (!isDataComplete(parsedData)) {
            console.warn(
              '[useCollaborationData] ⚠️ SessionStorage data incomplete, ignoring',
              parsedData
            );
            sessionStorage.removeItem(`collaboration_${id}`);
            return null;
          }

          // 清除 sessionStorage，避免下次误用
          sessionStorage.removeItem(`collaboration_${id}`);
          return parsedData;
        }
      } catch (error) {
        console.warn(
          '[useCollaborationData] Failed to parse sessionStorage data:',
          error
        );
      }

      // 如果 sessionStorage 没有，尝试从 localStorage 获取
      console.log(
        '[useCollaborationData] No sessionStorage data, trying localStorage for id:',
        id
      );
      const localStorageData = getCollaborationDataById(id);
      if (localStorageData) {
        console.log(
          '[useCollaborationData] Found data in localStorage:',
          localStorageData
        );

        // 🔧 【关键修复】检查 localStorage 数据完整性
        if (!isDataComplete(localStorageData)) {
          console.warn(
            '[useCollaborationData] ⚠️ LocalStorage data incomplete, ignoring',
            localStorageData
          );
          return null;
        }

        return localStorageData;
      }
    }

    return initialData;
  });

  // 调试信息
  console.log('[useCollaborationData] Final projectData:', projectData);
  console.log(
    '[useCollaborationData] Final projectData.roles:',
    projectData?.roles
  );
  console.log(
    '[useCollaborationData] Final projectData.collaborators:',
    projectData?.collaborators
  );
  console.log(
    '[useCollaborationData] Final projectData.vision?.collaborators:',
    projectData?.vision?.collaborators
  );
  console.log('[useCollaborationData] ID from URL:', id);
  console.log(
    '[useCollaborationData] Context data:',
    state.selectedCollaboration
  );
  console.log('[useCollaborationData] Location state (full):', location.state);
  console.log(
    '[useCollaborationData] Location state.project:',
    location.state?.project
  );
  console.log(
    '[useCollaborationData] Location state.image:',
    location.state?.image
  );
  console.log(
    '[useCollaborationData] Location state roles:',
    location.state?.project?.roles || location.state?.roles
  );

  // 状态管理
  const [appliedPositions, setAppliedPositions] = useState(new Set());
  const [isSaved, setIsSaved] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({
    email: '',
    portfolio: '',
    message: '',
  });
  const [hasSubmittedApplication, setHasSubmittedApplication] = useState(false);
  const [isApplicationApproved, setIsApplicationApproved] = useState(false); // 新增：检查申请是否被批准
  const [showWarning, setShowWarning] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelPositionId, setCancelPositionId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [positions, setPositions] = useState([]);
  const [project, setProject] = useState(null);
  // 🔧 优化：如果有初始数据，不显示 loading
  const [isLoading, setIsLoading] = useState(!projectData); // 只有在没有初始数据时才显示 loading

  // 处理项目数据并设置初始project状态
  useEffect(() => {
    // 🔧 【关键修复】如果 projectData 不完整，不设置 project 状态，等待 Supabase 数据
    if (projectData && !isDataComplete(projectData)) {
      console.warn(
        '[useCollaborationData] ⚠️ projectData incomplete, skipping processProjectData, waiting for Supabase...',
        projectData
      );
      return; // 不设置 project 状态，保持 loading 状态
    }

    const processedProject = processProjectData(projectData, location, state);
    console.log('[useCollaborationData] Processed project:', processedProject);
    console.log(
      '[useCollaborationData] Processed project.roles:',
      processedProject?.roles
    );

    // 如果处理后的项目没有角色数据，尝试从 localStorage 恢复
    if (!processedProject?.roles || processedProject.roles.length === 0) {
      console.log(
        '[useCollaborationData] Processed project has no roles, trying localStorage recovery...'
      );

      if (id) {
        const localStorageData = getCollaborationDataById(id);
        if (
          localStorageData &&
          localStorageData.roles &&
          localStorageData.roles.length > 0
        ) {
          console.log(
            '[useCollaborationData] Found roles in localStorage:',
            localStorageData.roles
          );

          // 更新 processedProject，添加角色数据
          const updatedProject = {
            ...processedProject,
            roles: localStorageData.roles,
          };
          console.log(
            '[useCollaborationData] Updated project with roles:',
            updatedProject
          );
          setProject(updatedProject);
          return;
        }
      }
    }

    setProject(processedProject);
  }, [projectData, location, state, id]);

  // 补充 author 信息（如果缺失）
  useEffect(() => {
    if (!project?.author?.id) return;

    // 检查 author 信息是否完整
    // 如果 name 或 avatar 缺失，即使 artist 有值也需要查询
    const hasName = project.author.name || project.author.artist;
    const hasAvatar = project.author.avatar || project.author.artistAvatar;
    const needsAuthorInfo = !hasName || !hasAvatar;

    if (needsAuthorInfo) {
      console.log(
        '[useCollaborationData] Author info incomplete, fetching from Supabase...',
        { authorId: project.author.id }
      );

      // 从 Supabase profiles 表查询 author 信息
      const fetchAuthorInfo = async () => {
        try {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('full_name, avatar_url')
            .eq('id', project.author.id)
            .single();

          if (!error && profile) {
            console.log(
              '[useCollaborationData] Fetched author profile:',
              profile
            );

            // 使用 full_name，如果为空则保持现有的 name/artist 值，最后回退到 'Unknown'
            const displayName =
              profile.full_name ||
              project.author.name ||
              project.author.artist ||
              'Unknown';

            // 更新 project 状态，补充 author 信息
            setProject(prevProject => ({
              ...prevProject,
              author: {
                ...prevProject.author,
                name: displayName,
                artist: displayName,
                avatar: profile.avatar_url || null,
                artistAvatar: profile.avatar_url || null,
              },
            }));
          } else {
            console.warn(
              '[useCollaborationData] Failed to fetch author profile:',
              error
            );
          }
        } catch (error) {
          console.error(
            '[useCollaborationData] Error fetching author info:',
            error
          );
        }
      };

      fetchAuthorInfo();
    }
  }, [project?.author?.id, project?.author?.name, project?.author?.artist]);

  // 处理数据加载和补充
  useEffect(() => {
    if (!id) {
      console.log('[useCollaborationData] No id, skipping Supabase fetch');
      return;
    }

    console.log('[useCollaborationData] useEffect triggered for id:', id);
    console.log('[useCollaborationData] Current projectData:', projectData);

    // 总是从 Supabase 获取最新数据，确保 collaborators 是最新的
    // 即使有初始数据，也应该刷新一次，因为可能有新批准的申请
    const fetchFromSupabase = async () => {
      // 🔧 【关键修复】如果 projectData 不完整（只有 title，其他字段都是空的），也要设置 loading
      const shouldShowLoading = !projectData || !isDataComplete(projectData);

      if (shouldShowLoading) {
        console.log(
          '[useCollaborationData] ⚠️ Data incomplete or missing, showing loading...'
        );
        setIsLoading(true);
      }
      try {
        console.log(
          '[useCollaborationData] 🔄 Fetching collaboration from Supabase for id:',
          id
        );
        const result = await getCollaborationById(id);

        console.log('[useCollaborationData] getCollaborationById result:', {
          success: result.success,
          hasData: !!result.data,
          error: result.error,
        });

        if (result.success && result.data) {
          console.log(
            '[useCollaborationData] ✅ Successfully fetched from Supabase:',
            result.data
          );
          console.log(
            '[useCollaborationData] 📋 Collaborators from Supabase:',
            result.data.collaborators
          );
          console.log(
            '[useCollaborationData] 📋 Vision.collaborators from Supabase:',
            result.data.vision?.collaborators
          );
          // 更新projectData状态，这会触发上面的useEffect重新处理数据
          setProjectData(result.data);
          setIsLoading(false); // 加载完成
        } else {
          console.warn(
            '[useCollaborationData] Failed to fetch from Supabase:',
            result.error
          );
          // 如果 Supabase 获取失败，且没有初始数据，再尝试从 localStorage 获取（作为回退）
          if (!projectData) {
            console.log(
              '[useCollaborationData] Falling back to localStorage for id:',
              id
            );

            // 尝试多种 ID 格式
            const possibleIds = [id, parseInt(id), id.toString()];
            let localStorageData = null;

            for (const testId of possibleIds) {
              console.log(
                `[useCollaborationData] Trying localStorage ID format: ${testId} (type: ${typeof testId})`
              );
              localStorageData = getCollaborationDataById(testId);
              if (localStorageData) {
                console.log(
                  `[useCollaborationData] Found localStorage data with ID: ${testId}`
                );
                break;
              }
            }

            if (localStorageData) {
              console.log(
                '[useCollaborationData] Using localStorage data as fallback:',
                localStorageData
              );
              setProjectData(localStorageData);
            } else {
              console.warn(
                '[useCollaborationData] No data found in localStorage either'
              );
            }
          }
          setIsLoading(false); // 即使失败也结束 loading
        }
      } catch (error) {
        console.error(
          '[useCollaborationData] Error fetching from Supabase:',
          error
        );
        // 发生错误时，如果没有初始数据，尝试从 localStorage 获取（作为回退）
        if (!projectData) {
          const localStorageData = getCollaborationDataById(id);
          if (localStorageData) {
            console.log(
              '[useCollaborationData] Using localStorage data as error fallback:',
              localStorageData
            );
            setProjectData(localStorageData);
          }
        }
        setIsLoading(false); // 错误时也结束 loading
      }
    };

    fetchFromSupabase();
  }, [id]); // 每次 id 变化时都从 Supabase 获取最新数据

  // 功能开关
  const enableProjectProgressBar = true;
  const enableAfterFinishedReview = true;
  const enableReviewApprovalPanel = false;

  // 监听用户切换事件
  useEffect(() => {
    const handleUserChange = () => {
      setCurrentUser(getCurrentUser());
    };

    window.addEventListener('user:changed', handleUserChange);
    return () => {
      window.removeEventListener('user:changed', handleUserChange);
    };
  }, []);

  // 加载职位数据并应用持久化状态
  useEffect(() => {
    // 🔧 【关键修复】如果 project 数据不完整，不加载职位数据
    if (!project || !project.id || !isDataComplete(project)) {
      console.warn(
        '[useCollaborationData] ⚠️ Project data incomplete, skipping positions load',
        { projectId: project?.id, hasRoles: !!project?.roles }
      );
      return;
    }

    console.log(
      '[useCollaborationData] useEffect triggered, project?.id:',
      project?.id
    );
    console.log('[useCollaborationData] project?.roles:', project?.roles);

    if (!project?.id) {
      console.log('[useCollaborationData] No project ID, returning early');
      return;
    }

    // 额外检查：如果 project 没有角色数据，尝试从 localStorage 恢复
    if (!project.roles || project.roles.length === 0) {
      console.log(
        '[useCollaborationData] Project has no roles, trying to recover from localStorage...'
      );

      // 尝试多种 ID 格式
      const possibleIds = [
        project.id,
        parseInt(project.id),
        project.id.toString(),
      ];
      let localStorageData = null;

      for (const testId of possibleIds) {
        console.log(
          `[useCollaborationData] Trying ID format: ${testId} (type: ${typeof testId})`
        );
        localStorageData = getCollaborationDataById(testId);
        if (
          localStorageData &&
          localStorageData.roles &&
          localStorageData.roles.length > 0
        ) {
          console.log(
            `[useCollaborationData] Found roles data with ID: ${testId}`
          );
          console.log(
            '[useCollaborationData] Found roles:',
            localStorageData.roles
          );

          // 更新 project 对象，添加角色数据
          const updatedProject = {
            ...project,
            roles: localStorageData.roles,
          };
          console.log(
            '[useCollaborationData] Updated project with roles:',
            updatedProject
          );
          setProject(updatedProject);
          // 不要 return，继续执行 loadPositionsWithStatus
        }
      }

      console.log('[useCollaborationData] No roles data found in localStorage');
    }

    const loadPositionsWithStatus = async () => {
      try {
        // 获取项目数据中的真实职位信息
        const defaultPositions = getPositionsData(project);

        // 获取持久化的职位状态
        const savedStatus = getAllPositionStatus(project.id);

        // 加载申请记录
        const savedApplications = await applicationService.getApplicationsData(
          project.id
        );

        // 更新申请记录中的用户姓名（如果需要）
        const updateUserNamesInApplications = async () => {
          if (savedApplications) {
            const currentUser = getCurrentUser();
            if (currentUser?.id) {
              try {
                const profileResult = await getProfile(currentUser.id);
                if (profileResult.success && profileResult.data?.fullName) {
                  const newName = profileResult.data.fullName;
                  // 检查是否需要更新
                  let needsUpdate = false;
                  Object.values(savedApplications).forEach(
                    positionApplications => {
                      positionApplications.forEach(application => {
                        if (
                          application.userId === currentUser.id &&
                          application.name !== newName
                        ) {
                          needsUpdate = true;
                        }
                      });
                    }
                  );

                  if (needsUpdate) {
                    await applicationService.updateApplicationUserName(
                      currentUser.id,
                      newName
                    );
                    // 重新获取更新后的数据
                    const updatedApplications =
                      await applicationService.getApplicationsData(project.id);
                    if (updatedApplications) {
                      Object.keys(updatedApplications).forEach(positionId => {
                        updatedApplications[positionId].forEach(application => {
                          if (application.userId === currentUser.id) {
                            application.name = newName;
                          }
                        });
                      });
                    }
                  }
                }
              } catch (error) {
                console.warn(
                  '[useCollaborationData] Failed to update application user names:',
                  error
                );
              }
            }
          }
        };

        // 执行异步更新
        updateUserNamesInApplications();

        // 恢复申请状态：从applicationStorage恢复已批准的申请状态
        const restoreApplicationStatus = () => {
          if (savedApplications) {
            Object.entries(savedApplications).forEach(
              ([positionId, applications]) => {
                applications.forEach(application => {
                  // 从applicationStorage检查是否已被批准
                  // 统一使用application.id作为键，如果没有则回退到userId
                  const applicantId = application.id || application.userId;
                  const storedStatus = getApplicationStatus(
                    positionId,
                    applicantId
                  );
                  if (storedStatus === 'approved') {
                    console.log(
                      `[useCollaborationData] Restoring approved status from storage for user ${application.userId} in position ${positionId}`
                    );
                    application.status = 'approved';
                  } else if (application.status === 'approved') {
                    // 如果applicationService中有approved状态，同步到applicationStorage
                    console.log(
                      `[useCollaborationData] Syncing approved status to storage for user ${application.userId} in position ${positionId}`
                    );
                    // 这里可以调用saveApplicationStatus，但为了避免循环依赖，我们先记录日志
                  }
                });
              }
            );
          }
        };

        restoreApplicationStatus();

        // 应用保存的状态和申请记录到职位数据
        const positionsWithStatus = defaultPositions.map(position => {
          const savedPositionApplications =
            savedApplications?.[position.id] || [];

          console.log(
            `[useCollaborationData] Position ${position.id} saved applications:`,
            savedPositionApplications
          );

          // 确保申请状态正确恢复
          const applicationsWithStatus = savedPositionApplications.map(app => {
            console.log(
              `[useCollaborationData] Application ${app.userId} status: ${app.status}`
            );
            return {
              ...app,
              status: app.status || 'pending', // 确保状态字段存在
            };
          });

          const result = {
            ...position,
            status: savedStatus[position.id] || position.status,
            // 合并保存的申请记录，确保状态正确
            applications: applicationsWithStatus,
          };

          console.log(
            `[useCollaborationData] Final position ${position.id} applications:`,
            result.applications
          );

          return result;
        });

        setPositions(positionsWithStatus);
      } catch (error) {
        console.error('Failed to load positions with status:', error);
        setPositions(getPositionsData(project));
      }
    };

    console.log('[useCollaborationData] About to call loadPositionsWithStatus');
    loadPositionsWithStatus();
    console.log('[useCollaborationData] loadPositionsWithStatus called!');
  }, [project?.id, project?.roles]);

  // 初始化已申请职位状态
  useEffect(() => {
    if (!project?.id || !currentUser?.id) return;

    const initializeAppliedPositions = async () => {
      try {
        // 获取已保存的申请记录
        const savedApplications = await applicationService.getApplicationsData(
          project.id
        );

        if (savedApplications) {
          // 检查当前用户已经申请过的职位
          const userAppliedPositions = new Set();

          Object.entries(savedApplications).forEach(
            ([positionId, applications]) => {
              // 检查该职位的申请记录中是否有当前用户
              const hasApplied = applications.some(
                app => app.userId === currentUser.id
              );
              if (hasApplied) {
                userAppliedPositions.add(parseInt(positionId));
              }
            }
          );

          // 更新已申请职位状态
          setAppliedPositions(userAppliedPositions);

          console.log(
            `[useCollaborationData] Initialized applied positions for user ${currentUser.id}:`,
            Array.from(userAppliedPositions)
          );
        }
      } catch (error) {
        console.error('Failed to initialize applied positions:', error);
      }
    };

    initializeAppliedPositions();
  }, [project?.id, currentUser?.id]);

  // 异步获取当前用户
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const user = await getCurrentUser();
      if (user) {
        setCurrentUser(user);
        console.log('[useCollaborationData] Loaded current user:', user.id);
      }
    };
    fetchCurrentUser();
  }, []);

  // 加载Apply Now表单数据
  useEffect(() => {
    console.log(
      '[useCollaborationData] loadApplyFormData useEffect triggered:',
      {
        'project?.id': project?.id,
        'currentUser?.id': currentUser?.id,
      }
    );

    if (!project?.id || !currentUser?.id) {
      console.log(
        '[useCollaborationData] Skipping loadApplyFormData: missing project.id or currentUser.id'
      );
      return;
    }

    const loadApplyFormData = async () => {
      try {
        console.log(
          '[useCollaborationData] Loading apply form data for project:',
          project.id,
          'user:',
          currentUser.id
        );

        const savedFormData = getApplyFormData(project.id, currentUser.id);
        if (savedFormData) {
          setApplyForm(savedFormData);
          const hasComplete = hasCompleteApplyFormData(
            project.id,
            currentUser.id
          );
          setHasSubmittedApplication(hasComplete);
          console.log(
            '[useCollaborationData] Loaded saved apply form data for user:',
            savedFormData,
            'hasComplete:',
            hasComplete
          );
        } else {
          // 如果没有表单数据，确保状态为 false
          setHasSubmittedApplication(false);
          console.log(
            '[useCollaborationData] No saved apply form data found for project:',
            project.id,
            'user:',
            currentUser.id
          );
        }

        // 检查申请是否被批准（通过检查通知）
        console.log(
          '[useCollaborationData] Checking approval notification for collaboration:',
          project.id
        );
        try {
          const { notificationService } = await import(
            '../../../services/notificationService'
          );
          const notificationsResult =
            await notificationService.getUserNotifications(currentUser.id);

          console.log(
            '[useCollaborationData] Notifications result:',
            notificationsResult.success,
            'count:',
            notificationsResult.data?.length
          );

          if (notificationsResult.success && notificationsResult.data) {
            // 查找是否有针对这个 collaboration 的 application_approved 通知
            const approvalNotification = notificationsResult.data.find(
              notif =>
                notif.type === 'collaboration' &&
                notif.meta?.action === 'application_approved' &&
                (notif.meta?.collaborationId === project.id ||
                  notif.projectId === project.id ||
                  notif.meta?.projectId === project.id)
            );

            console.log(
              '[useCollaborationData] Approval notification search result:',
              approvalNotification ? 'FOUND' : 'NOT FOUND',
              approvalNotification
            );

            if (approvalNotification) {
              console.log(
                '[useCollaborationData] ✅ Found approval notification for this collaboration:',
                approvalNotification
              );
              setIsApplicationApproved(true);
            } else {
              console.log(
                '[useCollaborationData] No approval notification found for collaboration:',
                project.id,
                'Available notifications:',
                notificationsResult.data
                  .filter(n => n.type === 'collaboration')
                  .map(n => ({
                    id: n.id,
                    action: n.meta?.action,
                    collaborationId:
                      n.meta?.collaborationId ||
                      n.projectId ||
                      n.meta?.projectId,
                  }))
              );
              setIsApplicationApproved(false);
            }
          } else {
            console.log(
              '[useCollaborationData] Failed to get notifications or no data:',
              notificationsResult
            );
            setIsApplicationApproved(false);
          }
        } catch (error) {
          console.error(
            '[useCollaborationData] Failed to check approval notification:',
            error
          );
          setIsApplicationApproved(false);
        }
      } catch (error) {
        console.error('Failed to load apply form data:', error);
      }
    };

    loadApplyFormData();
  }, [project?.id, currentUser?.id]);

  // 检查收藏状态
  useEffect(() => {
    if (!project?.id) return;

    const checkFavoriteStatus = async () => {
      try {
        const result = await favoritesService.checkFavoriteStatus(
          'collaboration',
          project.id
        );
        if (result.success) {
          setIsSaved(result.data.isFavorited);
        }
      } catch (error) {
        console.error('Failed to check favorite status:', error);
      }
    };

    checkFavoriteStatus();
  }, [project?.id]);

  // 更新项目数据
  const updateProjectData = useCallback(
    newProjectData => {
      console.log(
        '[useCollaborationData] updateProjectData called with:',
        newProjectData
      );
      console.log(
        '[useCollaborationData] New collaborators:',
        newProjectData?.collaborators
      );
      console.log(
        '[useCollaborationData] New vision.collaborators:',
        newProjectData?.vision?.collaborators
      );

      // 同时更新 projectData 和 project 状态
      // 先更新 projectData，这会触发 processProjectData 重新处理
      setProjectData(newProjectData);

      // 同时直接更新 project，确保 collaborators 立即显示
      // 注意：这里需要确保数据格式正确
      const processedProject = processProjectData(
        newProjectData,
        location,
        state
      );
      setProject(processedProject);

      console.log(
        '[useCollaborationData] ✅ Updated both projectData and project'
      );
      console.log(
        '[useCollaborationData] Final project.collaborators:',
        processedProject?.collaborators
      );
    },
    [location, state]
  );

  // 更新职位数据
  const updatePositions = useCallback(newPositions => {
    setPositions(newPositions);
    console.log('[useCollaborationData] Updated positions:', newPositions);
  }, []);

  // 保存协作数据到localStorage
  const saveCollaborationData = useCallback(async projectData => {
    try {
      console.log('[saveCollaborationData] Saving project data:', projectData);

      // 辅助函数：判断是否为需要移除的大数据URL
      const shouldRemoveImage = imgValue => {
        if (!imgValue || typeof imgValue !== 'string') return false;
        // 移除 base64 data URL 和 blob URL，但保留 IndexedDB key
        return (
          imgValue.startsWith('data:image/') ||
          imgValue.startsWith('blob:') ||
          imgValue.length > 10000 // 移除超长字符串（可能是base64）
        );
      };

      // 辅助函数：将 base64 data URL 存储到 IndexedDB 并返回 key
      const storeImageToIndexedDB = async (imgValue, collaborationId) => {
        if (!imgValue || !shouldRemoveImage(imgValue)) return null;

        try {
          const imageStorage = await import('../../../utils/indexedDB.js');
          const imageKey = `collaboration_${collaborationId}_poster_${Date.now()}`;

          // 如果是 base64 data URL，需要先转换为 Blob
          if (imgValue.startsWith('data:image/')) {
            const response = await fetch(imgValue);
            const blob = await response.blob();
            await imageStorage.default.storeImage(imageKey, blob);
            console.log(
              `[saveCollaborationData] Stored base64 image to IndexedDB with key: ${imageKey}`
            );
            return imageKey;
          }

          // 如果是 blob URL，直接获取 Blob
          if (imgValue.startsWith('blob:')) {
            const response = await fetch(imgValue);
            const blob = await response.blob();
            await imageStorage.default.storeImage(imageKey, blob);
            console.log(
              `[saveCollaborationData] Stored blob URL to IndexedDB with key: ${imageKey}`
            );
            return imageKey;
          }
        } catch (error) {
          console.error(
            '[saveCollaborationData] Failed to store image to IndexedDB:',
            error
          );
        }
        return null;
      };

      // 处理图片：如果是 base64/blob URL，先存储到 IndexedDB
      const collaborationId =
        projectData.id ||
        `collab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      let heroImageKey = projectData.heroImage;
      let posterPreviewKey = projectData.posterPreview;
      let imageKey = projectData.image;

      // 如果图片是 base64/blob URL，存储到 IndexedDB
      if (shouldRemoveImage(projectData.heroImage)) {
        heroImageKey = await storeImageToIndexedDB(
          projectData.heroImage,
          collaborationId
        );
      }
      if (shouldRemoveImage(projectData.posterPreview)) {
        posterPreviewKey = await storeImageToIndexedDB(
          projectData.posterPreview,
          collaborationId
        );
      }
      if (shouldRemoveImage(projectData.image)) {
        imageKey = await storeImageToIndexedDB(
          projectData.image,
          collaborationId
        );
      }

      // 确保 posterPreview 和 heroImage 有值（优先使用 posterPreview）
      if (!posterPreviewKey && heroImageKey) {
        posterPreviewKey = heroImageKey;
      }
      if (!heroImageKey && posterPreviewKey) {
        heroImageKey = posterPreviewKey;
      }
      if (!posterPreviewKey && !heroImageKey && imageKey) {
        posterPreviewKey = imageKey;
        heroImageKey = imageKey;
      }

      // 创建精简版projectData，保留IndexedDB key，移除base64图片数据
      const strippedProjectData = {
        ...projectData,
        // 保留 IndexedDB key，移除 base64/blob URL
        heroImage: heroImageKey || undefined,
        posterPreview: posterPreviewKey || undefined,
        image: imageKey || undefined,
        // 保留所有其他重要信息，包括initiator信息
        author: projectData.author, // 确保initiator信息不被丢失
        authorId: projectData.authorId,
        authorName: projectData.authorName,
        // 保留所有项目元数据
        id: projectData.id,
        title: projectData.title,
        description: projectData.description,
        collaborators: projectData.collaborators,
        roles: projectData.roles,
        vision: projectData.vision,
        duration: projectData.duration,
        teamSize: projectData.teamSize,
        tags: projectData.tags,
        status: projectData.status,
        postedTime: projectData.postedTime,
        applicationDeadline: projectData.applicationDeadline,
        meetingFrequency: projectData.meetingFrequency,
        // 保留其他可能的重要字段
        contactInfo: projectData.contactInfo,
        categories: projectData.categories,
        milestones: projectData.milestones,
      };

      console.log(
        '[saveCollaborationData] Stripped project data for storage:',
        strippedProjectData
      );
      console.log(
        '[saveCollaborationData] Project collaborators:',
        projectData?.collaborators
      );
      console.log(
        '[saveCollaborationData] Project vision collaborators:',
        projectData?.vision?.collaborators
      );
      console.log(
        '[saveCollaborationData] Project positions:',
        projectData?.roles
      );

      // 获取现有的协作数据
      const stored = localStorage.getItem('mock_collaborations');
      const collaborations = stored ? JSON.parse(stored) : [];
      console.log(
        '[saveCollaborationData] Existing collaborations:',
        collaborations
      );

      // 更新指定的协作项目
      const updatedCollaborations = collaborations.map(collab => {
        if (collab.id === strippedProjectData.id) {
          console.log(
            '[saveCollaborationData] Updating collaboration:',
            collab.id
          );
          console.log(
            '[saveCollaborationData] Old collaborators:',
            collab.collaborators
          );
          console.log(
            '[saveCollaborationData] New collaborators:',
            projectData.collaborators
          );

          // 安全的合并策略：只更新需要变化的字段，保留原有数据
          const updatedCollab = {
            ...collab, // 保留原有完整数据
            // 只更新collaborators和roles，不覆盖其他字段
            collaborators:
              strippedProjectData.collaborators || collab.collaborators || [],
            roles: strippedProjectData.roles || collab.roles || [],
            // 安全地更新vision中的collaborators
            vision: {
              ...collab.vision,
              collaborators:
                strippedProjectData.vision?.collaborators ||
                strippedProjectData.collaborators ||
                collab.vision?.collaborators ||
                [],
            },
            // 更新图片字段：如果新的数据有图片 key，使用新的；否则保留原有的
            heroImage:
              strippedProjectData.heroImage || collab.heroImage || null,
            posterPreview:
              strippedProjectData.posterPreview || collab.posterPreview || null,
            image: strippedProjectData.image || collab.image || null,
            // 保留所有原有的元数据，不覆盖
            author: collab.author, // 确保initiator信息不被覆盖
            authorId: collab.authorId,
            authorName: collab.authorName,
            authorAvatar: collab.authorAvatar,
            title: collab.title,
            description: collab.description,
            duration: collab.duration,
            teamSize: collab.teamSize,
            tags: collab.tags,
            status: collab.status,
            postedTime: collab.postedTime,
            applicationDeadline: collab.applicationDeadline,
            meetingFrequency: collab.meetingFrequency,
            contactInfo: collab.contactInfo,
            categories: collab.categories,
            milestones: collab.milestones,
          };

          console.log(
            '[saveCollaborationData] Updated collaboration:',
            updatedCollab
          );
          return updatedCollab;
        }
        return collab;
      });

      // 保存回localStorage，添加配额检查
      const dataToSave = JSON.stringify(updatedCollaborations);
      const dataSize = dataToSave.length;

      console.log(
        `[saveCollaborationData] Data size: ${(dataSize / 1024 / 1024).toFixed(
          2
        )} MB`
      );

      // 检查localStorage配额
      try {
        localStorage.setItem('mock_collaborations', dataToSave);
        console.log(
          '[saveCollaborationData] Successfully saved to localStorage'
        );
      } catch (quotaError) {
        if (quotaError.name === 'QuotaExceededError') {
          console.error(
            '[saveCollaborationData] localStorage quota exceeded, attempting smart cleanup...'
          );

          // 使用智能清理器清理空间
          try {
            const { default: smartDataCleaner } = await import(
              '../../../utils/smartDataCleaner.js'
            );

            // 执行智能清理
            const cleanupResult = await smartDataCleaner.manualCleanup([
              'avatarCache',
              'tempImages',
              'oldCollaborations',
              'draftData',
            ]);

            if (cleanupResult.success) {
              console.log(
                `[saveCollaborationData] Smart cleanup freed ${(
                  cleanupResult.freedSpace / 1024
                ).toFixed(1)}KB`
              );

              // 再次尝试保存
              localStorage.setItem('mock_collaborations', dataToSave);
              console.log(
                '[saveCollaborationData] Successfully saved after smart cleanup'
              );
            } else {
              throw new Error('Smart cleanup failed: ' + cleanupResult.error);
            }
          } catch (cleanupError) {
            console.error(
              '[saveCollaborationData] Smart cleanup failed, falling back to manual cleanup:',
              cleanupError
            );

            // 回退到手动清理
            const keysToRemove = [
              'tag.avatars',
              'tag.images',
              'portfolio_alice',
              'portfolio_bryan',
            ];
            keysToRemove.forEach(key => {
              if (localStorage.getItem(key)) {
                localStorage.removeItem(key);
                console.log(
                  `[saveCollaborationData] Removed ${key} to free space`
                );
              }
            });

            // 再次尝试保存
            localStorage.setItem('mock_collaborations', dataToSave);
            console.log(
              '[saveCollaborationData] Successfully saved after manual cleanup'
            );
          }
        } else {
          throw quotaError;
        }
      }

      // 验证保存的数据
      const verifyStored = localStorage.getItem('mock_collaborations');
      const verifyCollaborations = verifyStored ? JSON.parse(verifyStored) : [];
      const savedCollab = verifyCollaborations.find(
        c => c.id === strippedProjectData.id
      );
      console.log(
        '[saveCollaborationData] Verified saved collaborators:',
        savedCollab?.collaborators
      );
      console.log(
        '[saveCollaborationData] Verified saved vision collaborators:',
        savedCollab?.vision?.collaborators
      );
      console.log(
        '[saveCollaborationData] Verified saved positions:',
        savedCollab?.roles
      );
    } catch (error) {
      console.error(
        '[saveCollaborationData] Failed to save collaboration data:',
        error
      );
    }
  }, []);

  return {
    // 数据
    project,
    projectData,
    positions,
    appliedPositions,
    isLoading, // 导出 loading 状态
    isSaved,
    showApplyModal,
    applyForm,
    hasSubmittedApplication,
    isApplicationApproved,
    showWarning,
    showSuccessToast,
    showCancelModal,
    cancelPositionId,
    currentUser,

    // 功能开关
    enableProjectProgressBar,
    enableAfterFinishedReview,
    enableReviewApprovalPanel,

    // 状态更新函数
    setAppliedPositions,
    setIsSaved,
    setShowApplyModal,
    setApplyForm,
    setHasSubmittedApplication,
    setShowWarning,
    setShowSuccessToast,
    setShowCancelModal,
    setCancelPositionId,
    setCurrentUser,
    setPositions,

    // 新增的更新函数
    updateProjectData,
    updatePositions,
    saveCollaborationData,
  };
};
